import type { CanvasObject, PenStrokeCanvasObject } from "./canvas-object";

// キャンバスの有効領域。表示サイズだけでなく、新規作成・更新時の保存境界としても扱う。
export const CANVAS_WIDTH_PX = 10240;
export const CANVAS_HEIGHT_PX = 5760;

// 1 キャンバスあたりのオブジェクト数上限。UI と API で同じ値を参照する。
export const CANVAS_OBJECT_SOFT_LIMIT = 5_000;
export const CANVAS_OBJECT_HARD_LIMIT = 10_000;

export interface CanvasPoint {
  readonly x: number;
  readonly y: number;
}

export interface CanvasFrame {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export function isPointInsideCanvas(point: CanvasPoint): boolean {
  return (
    Number.isFinite(point.x) &&
    Number.isFinite(point.y) &&
    point.x >= 0 &&
    point.x <= CANVAS_WIDTH_PX &&
    point.y >= 0 &&
    point.y <= CANVAS_HEIGHT_PX
  );
}

export function clampPointToCanvas<T extends CanvasPoint>(point: T): T {
  return {
    ...point,
    x: clamp(point.x, 0, CANVAS_WIDTH_PX),
    y: clamp(point.y, 0, CANVAS_HEIGHT_PX),
  };
}

export function isFrameInsideCanvas(frame: CanvasFrame): boolean {
  return (
    Number.isFinite(frame.x) &&
    Number.isFinite(frame.y) &&
    Number.isFinite(frame.w) &&
    Number.isFinite(frame.h) &&
    frame.w >= 0 &&
    frame.h >= 0 &&
    frame.x >= 0 &&
    frame.y >= 0 &&
    frame.x + frame.w <= CANVAS_WIDTH_PX &&
    frame.y + frame.h <= CANVAS_HEIGHT_PX
  );
}

export function clampFrameToCanvas<T extends CanvasFrame>(frame: T): T {
  const w = clamp(frame.w, 0, CANVAS_WIDTH_PX);
  const h = clamp(frame.h, 0, CANVAS_HEIGHT_PX);
  return {
    ...frame,
    x: clamp(frame.x, 0, Math.max(0, CANVAS_WIDTH_PX - w)),
    y: clamp(frame.y, 0, Math.max(0, CANVAS_HEIGHT_PX - h)),
    w,
    h,
  };
}

export function isCanvasObjectInsideCanvas(object: CanvasObject): boolean {
  if (object.type === "pen-stroke") {
    return (
      isFrameInsideCanvas(object) &&
      object.points.every((point) => isPointInsideCanvas(point))
    );
  }
  return isFrameInsideCanvas(object);
}

export function clampCanvasObjectToCanvas<T extends CanvasObject>(object: T): T {
  if (object.type === "pen-stroke") {
    return clipPenStrokeObjectToCanvas(object) as T;
  }
  return {
    ...object,
    ...clampFrameToCanvas(object),
  };
}

export function clipPenStrokeObjectToCanvas(
  object: PenStrokeCanvasObject
): PenStrokeCanvasObject {
  const points = object.points.map((point) => clampPointToCanvas(point));
  return {
    ...object,
    ...computePenStrokeFrame(points, object.thickness),
    points,
  };
}

export function computePenStrokeFrame(
  points: readonly CanvasPoint[],
  thickness: number
): CanvasFrame {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  const first = points[0];
  if (!first) return { x: 0, y: 0, w: 0, h: 0 };

  let minX = first.x;
  let minY = first.y;
  let maxX = first.x;
  let maxY = first.y;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }

  const safeThickness = Number.isFinite(thickness) ? Math.max(0, thickness) : 0;
  const pad = safeThickness;
  const left = clamp(minX - pad, 0, CANVAS_WIDTH_PX);
  const top = clamp(minY - pad, 0, CANVAS_HEIGHT_PX);
  const right = clamp(maxX + pad, left, CANVAS_WIDTH_PX);
  const bottom = clamp(maxY + pad, top, CANVAS_HEIGHT_PX);
  return {
    x: left,
    y: top,
    w: right - left,
    h: bottom - top,
  };
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
